import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Moon, Clock, TrendingUp, Battery } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const mockSleepData = [
  { time: '22:00', quality: 30 },
  { time: '23:00', quality: 45 },
  { time: '00:00', quality: 80 },
  { time: '01:00', quality: 95 },
  { time: '02:00', quality: 90 },
  { time: '03:00', quality: 85 },
  { time: '04:00', quality: 75 },
  { time: '05:00', quality: 60 },
  { time: '06:00', quality: 40 },
  { time: '07:00', quality: 30 }
];

export function SleepAnalysis() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <div className="flex items-center gap-2">
          <Moon className="w-6 h-6 text-purple-600" />
          <h2 className="text-xl font-bold">Analyse du Sommeil</h2>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Clock className="w-4 h-4" />
          <span>Dernière nuit</span>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Sleep Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-4 h-4 text-purple-600" />
                <span className="text-sm font-medium">Durée</span>
              </div>
              <span className="text-2xl font-bold text-purple-600">8h 12m</span>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Moon className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium">Qualité</span>
              </div>
              <span className="text-2xl font-bold text-blue-600">85%</span>
            </div>
            
            <div className="p-4 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Battery className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium">Récupération</span>
              </div>
              <span className="text-2xl font-bold text-green-600">92%</span>
            </div>
          </div>

          {/* Sleep Graph */}
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mockSleepData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="time" 
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                />
                <YAxis 
                  stroke="#6b7280"
                  tick={{ fontSize: 12 }}
                  domain={[0, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    padding: '0.5rem'
                  }}
                  formatter={(value: number) => [`${value}%`, 'Qualité']}
                />
                <Line
                  type="monotone"
                  dataKey="quality"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Sleep Phases */}
          <div className="space-y-4">
            <h3 className="font-medium">Phases de sommeil</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-purple-600" />
                  <span>Sommeil profond</span>
                </div>
                <span className="font-medium">2h 45m</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-600" />
                  <span>Sommeil paradoxal</span>
                </div>
                <span className="font-medium">1h 30m</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span>Sommeil léger</span>
                </div>
                <span className="font-medium">3h 57m</span>
              </div>
            </div>
          </div>

          {/* Recommendations */}
          <div className="p-4 bg-purple-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              <h3 className="font-medium">Recommandations</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-600 mt-1.5" />
                <span>Votre cycle de sommeil est optimal. Maintenez cet horaire de coucher.</span>
              </li>
              <li className="flex items-start gap-2">
                <div className="w-2 h-2 rounded-full bg-purple-600 mt-1.5" />
                <span>Augmentez légèrement la température de la chambre de 1-2°C pour améliorer le sommeil paradoxal.</span>
              </li>
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}